import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { FaEdit } from "react-icons/fa";
import useAccess from "../../hooks/useAccess";
import "../../styles/Form.css";
import "../../styles/AdminGroupPage.css";
import DataTable from "../../components/DataTable";
import StyledSelect from "../../components/StyledSelect";
import ReportFilterPanel from "../../components/ReportFilterPanel";
import AdminPageHeader from "../../components/AdminPageHeader";
import "../../styles/AdministrationPages.css";

function AdminGroupPage() {
  const BASE_URL = process.env.REACT_APP_API_BASE_URL;
  const navigate = useNavigate();

  const [groups, setGroups] = useState([]);
  const [users, setUsers] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("All");

  const { hasPermission } = useAccess();

  const PERMISSIONS = {
    groups: {
      create: hasPermission("GroupPage", "Group", "edit"),
      edit: hasPermission("GroupPage", "Group", "edit"),
      delete: hasPermission("GroupPage", "Group", "delete"),
    },
  };

  useEffect(() => {
    axios
      .get(`${BASE_URL}/api/admin-groups`, {
        headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
      })
      .then((res) => setGroups(res.data))
      .catch((err) => console.error("Failed to load groups", err));

    axios
      .get(`${BASE_URL}/api/users`, {
        headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
      })
      .then((res) => setUsers(res.data))
      .catch((err) => console.error("Failed to load users", err));
  }, [BASE_URL]);

  const parseEmpGroups = (raw) => {
    if (!raw) return [];
    try {
      const parsed = typeof raw === "string" ? JSON.parse(raw.trim()) : raw;
      return Array.isArray(parsed) ? parsed.map(Number) : [];
    } catch {
      return [];
    }
  };

  const getUsersByGroupId = (groupId) =>
    users.filter((user) => parseEmpGroups(user.emp_groups).includes(Number(groupId)));

  const filteredGroups = useMemo(
    () =>
      groups.filter((group) => {
        const matchesSearch =
          group.pg_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
          (group.pg_email || "").toLowerCase().includes(searchTerm.toLowerCase());
        const matchesStatus =
          statusFilter === "All" ||
          (statusFilter === "Active" && group.pg_status === 1) ||
          (statusFilter === "Inactive" && group.pg_status === 0);
        return matchesSearch && matchesStatus;
      }),
    [groups, searchTerm, statusFilter]
  );

  const handleDelete = async (group) => {
    if (!PERMISSIONS.groups.delete) return;
    if (!window.confirm(`Delete the group "${group.pg_name}"? This will remove it from all assigned users.`)) {
      return;
    }

    try {
      await axios.delete(`${BASE_URL}/api/admin-groups/${group.pg_id}`, {
        headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
      });
      setGroups((current) =>
        current.filter((item) => item.pg_id !== group.pg_id),
      );
    } catch (error) {
      console.error("Failed to delete group", error);
      alert(error.response?.data?.message || "Failed to delete group");
    }
  };

  const columns = [
    {
      key: "rowNumber",
      label: "#",
      width: "72px",
      getSortValue: (row) => row.pg_id,
      render: (_row, index) => index + 1,
    },
    {
      key: "pg_name",
      label: "Name",
      width: "252px",
      render: (row) => <span className="fw-bold">{row.pg_name}</span>,
    },
    {
      key: "members",
      label: "Users",
      getSortValue: (row) => getUsersByGroupId(row.pg_id).length,
      render: (row) => {
        const groupUsers = getUsersByGroupId(row.pg_id);
        if (!groupUsers.length) {
          return <span className="text-muted">No members</span>;
        }

        const names = groupUsers.map((user) => user.full_name);
        const visibleNames = names.slice(0, 5);
        const remainingCount = names.length - visibleNames.length;
        return (
          <div className="group-members-preview" title={names.join(", ")}>
            <span className="group-members-names">{visibleNames.join(", ")}</span>
            {remainingCount > 0 && (
              <span className="group-members-more">+{remainingCount} more</span>
            )}
          </div>
        );
      },
    },
    {
      key: "pg_status",
      label: "Status",
      width: "14%",
      headerClassName: "admin-status-column text-center",
      cellClassName: "admin-status-column",
      getSortValue: (row) => (row.pg_status === 1 ? "Active" : "Inactive"),
      render: (row) => (
        <span className={`status-badge ${row.pg_status === 1 ? "active" : "inactive"}`}>
          {row.pg_status === 1 ? "Active" : "Inactive"}
        </span>
      ),
    },
    {
      key: "actions",
      label: "Actions",
      sortable: false,
      width: "12%",
      headerClassName: "admin-action-column text-center",
      cellClassName: "admin-action-column",
      render: (row) => (
        <div className="assignment-action-group">
          <button
            className="app-table-action-button is-edit"
            onClick={() => navigate(`/admin-groups/${row.pg_id}`)}
            disabled={!PERMISSIONS.groups.edit}
          >
            <FaEdit />
          </button>
          {PERMISSIONS.groups.delete && (
            <button
              className="app-table-action-button is-delete"
              onClick={() => handleDelete(row)}
              title="Delete group"
              aria-label={`Delete ${row.pg_name}`}
            >
              <i className="fas fa-trash" aria-hidden="true" />
            </button>
          )}
        </div>
      ),
    },
  ];

  return (
    <div className="admin-standard-page admin-group-page">
      <AdminPageHeader
        icon="fas fa-users"
        title="Group Management"
        description="Organize users into reusable groups and control group availability."
        actions={PERMISSIONS.groups.create && (
              <button
                className="btn btn-primary dashboard-add-button"
                onClick={() => navigate("/admin-groups/create")}
              >
                Add Group <i className="fas fa-plus" aria-hidden="true" />
              </button>
        )}
      />

      <section className="admin-standard-workspace">
        <div className="admin-standard-workspace-body">

          <ReportFilterPanel>
            <div className="admin-standard-filter-grid admin-group-filters">
              <label className="admin-standard-filter-field">
                <span>Search</span>
            <input
              type="search"
              className="form-control"
              placeholder="Group name or email..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
              </label>
              <label className="admin-standard-filter-field">
                <span>Status</span>
            <StyledSelect
              value={statusFilter}
              onChange={setStatusFilter}
              options={[
                { value: "All", label: "All" },
                { value: "Active", label: "Active" },
                { value: "Inactive", label: "Inactive" },
              ]}
              minWidth={150}
              ariaLabel="Filter groups by status"
            />
              </label>
            </div>
          </ReportFilterPanel>

          <DataTable
            columns={columns}
            data={filteredGroups}
            rowKey="pg_id"
            itemsPerPage={15}
            tableClassName="admin-group-table"
            emptyMessage="No groups found."
          />
        </div>
      </section>
    </div>
  );
}

export default AdminGroupPage;
